import { useForm, useWatch } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  useAcceptProductsMutation,
  useFindSimilarProductsQuery,
  useGetProductByIdQuery,
  useGetProductListsQuery,
  useGetProductSuppliersQuery,
  useUpdateProductBrandMutation,
  useUpdateProductCategoryMutation,
  useUpdateProductMutation,
} from '@app/api/Products';
import type {
  FindSimilarProductsParams,
  ModelProduct,
  ModelProductsAcceptance,
} from '@app/api/Products';
import { PRODUCT_DIRECTORY, PRODUCTS_PATH, SUCCESS } from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import type { ProductImageFile } from '@app/pages/Warehouses/Products/components/AddProduct/components/ProductImagesUploader/types';
import type {
  AddProductFormValues,
  FastCreationModalProps,
  UpdateBrandHandlerProps,
} from '@app/pages/Warehouses/Products/components/AddProduct/types';
import { FastCreationModalTypes } from '@app/pages/Warehouses/Products/components/AddProduct/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import { useGetWarehousesQuery } from '@app/services/rtkQuery/Warehouses/api';
import { useErrorHandler } from '@app/shared/hooks';
import { limitDecimals } from '@app/shared/utils';
import type { Options } from '@app/types';

export const useAddProduct = () => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const [searchParams] = useSearchParams();
  const [isOpenDeleteProductModal, setIsOpenDeleteProductModal] =
    useState(false);

  const editableProductId = searchParams.get('id')
    ? Number(searchParams.get('id'))
    : null;

  const [fileList, setFileList] = useState<ProductImageFile[]>([]);
  const [isFastCreationModalOpen, setIsFastCreationModalOpen] = useState(false);
  const [fastCreationModalProps, setFastCreationModalProps] =
    useState<FastCreationModalProps | null>(null);

  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const [form] = useForm<AddProductFormValues>();
  const [fastCreationModalForm] = useForm();
  const { setFieldValue, getFieldValue } = form;
  const formValues = useWatch([], form);

  const [initialFiles, setInitialFiles] = useState<ProductImageFile[]>([]);

  const {
    userAccessRights: {
      Products: {
        IsProductCreate,
        IsProductEdit,
        IsProductDelete,
        IsProductCategoryCreate,
      },
      Warehouse: { IsProductItemsAccept },
    },
  } = useNewUserAccessRights();

  const {
    data: suppliers,
    isLoading: isLoadingSuppliers,
    isUninitialized: isUninitializedSuppliers,
    isFetching: isFetchingSuppliers,
    isError: isErrorSuppliers,
    error: errorSuppliers,
  } = useGetProductSuppliersQuery(
    { ActiveBranchId },
    { skip: !ActiveBranchId }
  );

  const {
    data: warehouses,
    isLoading: isLoadingWarehouses,
    isUninitialized: isUninitializedWarehouses,
    isFetching: isFetchingWarehouses,
    isError: isErrorWarehouses,
    error: errorWarehouses,
  } = useGetWarehousesQuery({ ActiveBranchId }, { skip: !ActiveBranchId });

  const {
    data: editableProduct,
    error: getProductByIdError,
    isError: isErrorGetProductById,
    isFetching: isEditableProductFetching,
    isLoading: isEditableProductLoading,
    isUninitialized: isEditableProductUninitialized,
  } = useGetProductByIdQuery(
    { ActiveBranchId, productId: editableProductId },
    {
      skip: !editableProductId || !ActiveBranchId,
      refetchOnMountOrArgChange: true,
    }
  );

  const skipGetProductLists = useMemo(() => {
    if (!ActiveBranchId) return true;

    if (editableProductId && !editableProduct) return true;

    return false;
  }, [ActiveBranchId, editableProductId, editableProduct]);

  const {
    data: productLists,
    error: getProductListsError,
    isError: isErrorGetProductLists,
    refetch: refetchProductLists,
  } = useGetProductListsQuery(
    {
      ActiveBranchId,
      brandId: editableProductId
        ? editableProduct?.ProductBrandId ?? null
        : null,
      categoryId: editableProductId
        ? editableProduct?.ProductCategoryId ?? null
        : null,
    },
    { skip: skipGetProductLists }
  );

  const [similarProductsFilters, setSimilarProductsFilters] =
    useState<FindSimilarProductsParams>();

  const onChangeSimilarProductsFilters = useCallback(() => {
    const unsavedFilters: FindSimilarProductsParams = {
      Name: formValues?.Name ?? null,
      NameShort: formValues?.NameShort ?? null,
      Article: formValues?.Article ?? null,
      BarCode: formValues?.BarCode ?? null,
      UnitOfMeasurementId: formValues?.UnitOfMeasurementId ?? null,
      UnitOfMeasurement: productLists?.UnitsOfMeasurements?.find(
        (unit) => unit?.Id === formValues?.UnitOfMeasurementId
      )?.IdS,
      ProductCategoryId: formValues?.ProductCategoryId ?? null,
      ProductBrandId: formValues?.ProductBrandId ?? null,
    };

    if (!isEqual(similarProductsFilters, unsavedFilters)) {
      setSimilarProductsFilters(unsavedFilters);
    }
  }, [similarProductsFilters, formValues, productLists]);

  const {
    data: similarProducts,
    isLoading: _isSimilarProductsLoading,
    isFetching: isSimilarProductsFetching,
    isError: isSimilarProductsError,
    error: similarProductsError,
  } = useFindSimilarProductsQuery({
    ActiveBranchId,
    ...similarProductsFilters,
  });

  const [updateProduct, { isLoading }] = useUpdateProductMutation();
  const [acceptProducts, { isLoading: isLoadingAcceptProducts }] =
    useAcceptProductsMutation();
  const [updateBrand, { isLoading: isUpdateBrandLoading }] =
    useUpdateProductBrandMutation();
  const [updateProductCategory, { isLoading: isUpdateProductCategoryLoading }] =
    useUpdateProductCategoryMutation();

  useErrorHandler([
    { flag: isErrorSuppliers, error: errorSuppliers },
    { flag: isErrorWarehouses, error: errorWarehouses },
    { flag: isErrorGetProductById, error: getProductByIdError },
    { flag: isErrorGetProductLists, error: getProductListsError },
    { flag: isSimilarProductsError, error: similarProductsError },
  ]);

  const initForm = useCallback(() => {
    if (!editableProduct) {
      return form.resetFields();
    }

    const {
      Name,
      NameShort,
      Article,
      BarCode,
      ProductCategoryId,
      ProductBrandId,
      UnitOfMeasurementId,
      Amount,
      Comment,
      ReadFiles,
    } = editableProduct;

    form.setFieldsValue({
      Name: Name || '',
      NameShort: NameShort,
      Article: Article,
      BarCode: BarCode,
      ProductCategoryId: ProductCategoryId,
      ProductBrandId: ProductBrandId,
      UnitOfMeasurementId: UnitOfMeasurementId || null,
      Amount: String(Amount),
      Comment: Comment,
    });

    const initialFiles =
      ReadFiles?.map((file) => ({
        uid: file.Id.toString(),
        name: file.FileName ?? '',
        originFileObj: undefined,
        url: `data:image/jpeg;base64,${file.Thumbnail}`,
      })) || [];
    setFileList(initialFiles);
    setInitialFiles(initialFiles);
  }, [editableProduct, form]);

  const convertFileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const [, base64String] = (reader.result as string).split(',');
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  }, []);

  const prepareEditFiles = useCallback(async () => {
    return Promise.all(
      fileList.map(async ({ uid, name, originFileObj }) => {
        if (
          initialFiles.find(({ uid: initialFileId }) => initialFileId === uid)
        ) {
          {
            return {
              Id: Number(uid),
            };
          }
        }

        return {
          Id: 0,
          Name: name,
          Data: originFileObj ? await convertFileToBase64(originFileObj) : null,
        };
      })
    );
  }, [convertFileToBase64, fileList, initialFiles]);

  const acceptProductsHandler = useCallback(
    async (data: ModelProductsAcceptance) => {
      try {
        await acceptProducts({
          data,
          ActiveBranchId,
        }).unwrap();

        showNotification('Приход успешно оформлен', 'success');

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [acceptProducts, showNotification, ActiveBranchId]
  );

  const showProductAcceptItems = useMemo(() => {
    return IsProductItemsAccept && !editableProductId;
  }, [editableProductId, IsProductItemsAccept]);

  const handleSaveProduct = useCallback(
    async (values: AddProductFormValues) => {
      try {
        const {
          WarehouseId,
          SupplierId,
          Count,
          PriceOfPurchase,
          PriceOfSale,
          Name,
          NameShort,
          Article,
          BarCode,
          ProductCategoryId,
          ProductBrandId,
          UnitOfMeasurementId,
          Amount,
          Comment,
        } = values;

        const UnitOfMeasurement = productLists?.UnitsOfMeasurements?.find(
          (unit) => unit.Id === UnitOfMeasurementId
        )?.IdS;

        if (!UnitOfMeasurement) {
          showNotification('Единица измерения не найдена', 'warning');
          return;
        }

        if (!UnitOfMeasurementId) {
          showNotification('Единица измерения не найдена', 'warning');
          return;
        }

        const editFiles = await prepareEditFiles();

        const data: ModelProduct = {
          Id: editableProductId ?? 0,
          Name,
          NameShort,
          Article,
          BarCode,
          ProductCategoryId,
          ProductBrandId,
          UnitOfMeasurement,
          UnitOfMeasurementId,
          Amount: Amount ? Number(Amount) : null,
          Comment,
          EditFiles: editFiles,
        };

        const { Id } = await updateProduct({
          data,
          ActiveBranchId,
        }).unwrap();

        if (showProductAcceptItems && WarehouseId) {
          await acceptProductsHandler({
            DateAccepted: dayjs().format('YYYY-MM-DD'),
            WarehouseId: WarehouseId ?? 0,
            SupplierId: SupplierId,
            Products: [
              {
                ProductId: Id,
                Count: Count ?? 0,
                PriceOfPurchase: PriceOfPurchase ?? 0,
                PriceOfSale: PriceOfSale ?? 0,
              },
            ],
          });
        }

        const action = editableProductId ? 'обновлён' : 'добавлен';

        showNotification(`Товар успешно ${action}`, SUCCESS);
        navigate(PRODUCTS_PATH);
      } catch (error) {
        showNotification(error);
      }
    },
    [
      editableProductId,
      showProductAcceptItems,
      ActiveBranchId,
      prepareEditFiles,
      acceptProductsHandler,
      navigate,
      showNotification,
      updateProduct,
      productLists?.UnitsOfMeasurements,
    ]
  );

  const calculatePriceOfSale = useCallback(
    (markup: number | null) => {
      const priceOfPurchase = getFieldValue('PriceOfPurchase');

      if (markup === null) {
        setFieldValue('PriceOfSale', priceOfPurchase);
        return;
      }

      const value = (priceOfPurchase || 0) + (priceOfPurchase * markup) / 100;
      if (value < 0 || !isFinite(value) || isNaN(value)) {
        setFieldValue('PriceOfSale', 0);
        return;
      }

      if (value === Infinity) {
        setFieldValue('PriceOfSale', 0);
      }

      setFieldValue('PriceOfSale', value);
    },
    [getFieldValue, setFieldValue]
  );

  const deleteProductHandler = useCallback(async () => {
    if (!editableProductId) {
      return showNotification('Не удалось удалить товар', 'frontWarning');
    }

    if (!editableProduct) {
      return showNotification('Не удалось удалить товар', 'frontWarning');
    }

    try {
      await updateProduct({
        data: { ...editableProduct, Id: editableProductId, IsDeleted: true },
        ActiveBranchId,
      }).unwrap();

      showNotification('Товар успешно удалён', 'success');
      navigate(PRODUCT_DIRECTORY);
    } catch (error) {
      showNotification(error, 'warning');
    }
  }, [
    editableProductId,
    showNotification,
    ActiveBranchId,
    updateProduct,
    navigate,
    editableProduct,
  ]);

  const calculateMarkUp = useCallback(
    (priceOfSale?: number, priceOfPurchase?: number) => {
      const sale = Number(priceOfSale ?? 0);
      const purchase = Number(priceOfPurchase ?? 0);

      if (purchase <= 0) {
        setFieldValue('Markup', 0);
        return;
      }

      const markup = ((sale - purchase) / purchase) * 100;

      const validMarkup =
        Number.isFinite(markup) && !Number.isNaN(markup)
          ? limitDecimals(markup)
          : 0;

      setFieldValue('Markup', validMarkup);
    },
    [setFieldValue]
  );

  const handleCancel = useCallback(() => {
    navigate(PRODUCTS_PATH);
  }, [navigate]);

  const openDeleteProductModal = () => setIsOpenDeleteProductModal(true);

  const suppliersOptions: Options = useMemo(
    () =>
      suppliers?.map(({ Name, Id }) => ({
        label: Name,
        value: Id,
      })) || [],
    [suppliers]
  );

  const warehousesOptions: Options = useMemo(
    () =>
      warehouses?.map(({ Name, Id }) => ({
        label: Name,
        value: Id,
      })) || [],
    [warehouses]
  );

  const productCategoriesOptions: Options = useMemo(() => {
    if (!productLists) return [];

    const { ProductCategories } = productLists;

    if (!ProductCategories) return [];

    return ProductCategories.map(({ Id, Name }) => ({
      label: Name,
      value: Id,
    }));
  }, [productLists]);

  const productBrandsOptions: Options = useMemo(() => {
    if (!productLists) return [];

    const { ProductBrands } = productLists;

    if (!ProductBrands) return [];

    return ProductBrands.map(({ Id, Name }) => ({
      label: Name,
      value: Id,
    }));
  }, [productLists]);

  const unitsOfMeasurementsOptions: Options = useMemo(() => {
    if (!productLists) return [];

    const { UnitsOfMeasurements } = productLists;

    if (!UnitsOfMeasurements) return [];

    return UnitsOfMeasurements.map(({ Id, Name }) => ({
      label: Name,
      value: Id,
    }));
  }, [productLists]);

  const isPageForbidden = useMemo(() => {
    if (editableProductId && editableProduct) {
      return !IsProductEdit;
    }
    return !IsProductCreate;
  }, [editableProductId, editableProduct, IsProductEdit, IsProductCreate]);

  const isPageLoading = useMemo(() => {
    const isEditLoading = editableProductId
      ? isEditableProductUninitialized ||
        isEditableProductLoading ||
        isEditableProductFetching
      : false;
    return (
      isLoading ||
      isLoadingAcceptProducts ||
      isLoadingSuppliers ||
      isUninitializedSuppliers ||
      isFetchingSuppliers ||
      isLoadingWarehouses ||
      isUninitializedWarehouses ||
      isFetchingWarehouses ||
      isEditLoading
    );
  }, [
    isLoading,
    isLoadingAcceptProducts,
    isLoadingSuppliers,
    isUninitializedSuppliers,
    isFetchingSuppliers,
    isLoadingWarehouses,
    isUninitializedWarehouses,
    isFetchingWarehouses,
    isEditableProductUninitialized,
    isEditableProductLoading,
    isEditableProductFetching,
    editableProductId,
  ]);

  const closeFastCreationModal = useCallback(() => {
    setIsFastCreationModalOpen(false);
    setFastCreationModalProps(null);
    fastCreationModalForm.resetFields();
  }, [fastCreationModalForm]);

  const updateBrandHandler = useCallback(
    async ({ Name, Description }: UpdateBrandHandlerProps) => {
      try {
        const { Id } = await updateBrand({
          ActiveBranchId,
          data: {
            Id: 0,
            Name,
            Comment: Description,
            IsDeleted: false,
            CountProducts: 0,
          },
        }).unwrap();

        showNotification('Бренд успешно добавлен', SUCCESS);

        closeFastCreationModal();

        await refetchProductLists();

        form.setFieldValue('ProductBrandId', Id);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [
      updateBrand,
      ActiveBranchId,
      showNotification,
      closeFastCreationModal,
      refetchProductLists,
      form,
    ]
  );

  const updateProductCategoryHandler = useCallback(
    async ({ Name, Description }: UpdateBrandHandlerProps) => {
      try {
        const { Id } = await updateProductCategory({
          ActiveBranchId,
          data: {
            Id: 0,
            Name,
            Comment: Description,
            IsDeleted: false,
          },
        }).unwrap();

        showNotification('Категория товара успешно добавлена', SUCCESS);

        closeFastCreationModal();

        await refetchProductLists();

        form.setFieldValue('ProductCategoryId', Id);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [
      updateProductCategory,
      ActiveBranchId,
      showNotification,
      closeFastCreationModal,
      refetchProductLists,
      form,
    ]
  );

  const showFastCreationModal = useCallback(
    (modalType: FastCreationModalTypes) => {
      switch (modalType) {
        case FastCreationModalTypes.ADD_PRODUCT_BRAND:
          setFastCreationModalProps({
            title: 'Добавить бренд',
            finishModal: updateBrandHandler,
          });
          break;
        case FastCreationModalTypes.ADD_PRODUCT_CATEGORY:
          setFastCreationModalProps({
            title: 'Добавить категорию товара',
            finishModal: updateProductCategoryHandler,
          });
          break;
      }

      setIsFastCreationModalOpen(true);
    },
    [updateBrandHandler, updateProductCategoryHandler]
  );

  const showDeleteButton = useMemo(() => {
    if (!editableProductId || !editableProduct) return false;

    return IsProductDelete;
  }, [editableProductId, editableProduct, IsProductDelete]);

  const disabledAmountField = useMemo(() => {
    if (!editableProductId || !editableProduct) return false;
    if (
      editableProduct?.Amount === null ||
      editableProduct?.Amount === undefined
    )
      return false;
    return true;
  }, [editableProductId, editableProduct]);

  const disabledUnitOfMeasurementSelect = useMemo(() => {
    if (!editableProductId || !editableProduct) return false;
    if (
      editableProduct?.UnitOfMeasurementId === null ||
      editableProduct?.UnitOfMeasurementId === undefined
    )
      return false;
    return true;
  }, [editableProductId, editableProduct]);

  const isSimilarProductsLoading = useMemo(() => {
    return isSimilarProductsFetching || _isSimilarProductsLoading;
  }, [isSimilarProductsFetching, _isSimilarProductsLoading]);

  return {
    form,
    handleSaveProduct,
    fileList,
    setFileList,
    handleCancel,
    suppliersOptions,
    isPageForbidden,
    warehousesOptions,
    calculatePriceOfSale,
    calculateMarkUp,
    isPageLoading,
    productCategoriesOptions,
    productBrandsOptions,
    unitsOfMeasurementsOptions,
    showDeleteButton,
    openDeleteProductModal,
    isOpenDeleteProductModal,
    setIsOpenDeleteProductModal,
    deleteProductHandler,
    initForm,
    showProductAcceptItems,
    IsProductCreate,
    showFastCreationModal,
    isFastCreationModalOpen,
    fastCreationModalForm,
    isUpdateBrandLoading,
    IsProductCategoryCreate,
    closeFastCreationModal,
    fastCreationModalProps,
    isUpdateProductCategoryLoading,
    disabledAmountField,
    disabledUnitOfMeasurementSelect,
    formValues,
    editableProductId,
    editableProduct,
    isSimilarProductsLoading,
    similarProducts,
    onChangeSimilarProductsFilters,
  };
};
